/**
 * The lab controller: scenarios, runs, pods, health, assist, injection.
 *
 * Two backends, one module, and the split is not arbitrary:
 *
 * **Operations go to `/api/dcloud/lab/`** - toolbox_dcloud owns them because they need row
 * locks on the lab schema (`lease_steps` takes work under SELECT ... FOR UPDATE SKIP LOCKED,
 * and a lock cannot be held across an HTTP call). Those endpoints do things: open a run,
 * claim a pod, fire a trigger, push a step at running labs.
 *
 * **Authoring goes to `/api/db/lab-*`** - toolbox_db owns the tables, the serializers and the
 * generic CRUD they are registered for. The lab controller deliberately has no authoring
 * endpoints: keeping authoring and distribution apart means a half-written step cannot go out
 * because somebody hit save. So a step is *written* through `db/lab-scenario-step/` and
 * *pushed* through `dcloud/lab/injections/`, and those are two different actions in the UI too.
 *
 * Requests reuse `dcloudFetch` rather than growing a second request path, per its own
 * contract. Two things it does are worth knowing here:
 *
 * - It appends `?email=`. The lab controller ignores it - `lab/identity.py` resolves the
 *   caller from the Toolbox JWT and has no header or query-parameter fallback, on purpose
 *   ("an endpoint that trusts X-User-Email is an endpoint where anybody can act as anybody").
 *   So nothing here passes a caller email; the token decides who you are.
 * - It appends `?preferred_data_center=` unless told not to. Lab routes are Toolbox routes,
 *   not a dCloud passthrough, so every call here sets `skipDataCenter`.
 */

import { dcloudFetch, DcloudRequestMethod, DCLOUD_DATA_CENTERS } from './dcloudService';
import {
  deleteItem,
  fetchItem,
  fetchItems,
  getApiHeaders,
  patchItem,
  postItem,
} from './queries';

// -----------------------------------------------------------------------------
// Request paths
// -----------------------------------------------------------------------------

const LAB_BASE = 'dcloud/lab';

/** The lab controller. See the module docstring for why `skipDataCenter` is always set. */
async function labFetch<T = any>(
  method: DcloudRequestMethod,
  path: string,
  options: { params?: Record<string, any>; body?: any; signal?: AbortSignal } = {},
): Promise<T> {
  const { data } = await dcloudFetch<T>(method, `${LAB_BASE}/${path}`, {
    ...options,
    skipDataCenter: true,
  });

  return data;
}

/** Generic-CRUD table names for the rows this UI authors. */
export const LAB_TABLES = {
  /**
   * The top-level authored unit. A lab is a composition of scenarios plus the per-data-centre
   * bindings that say where it is delivered; it owns no automation of its own.
   */
  lab: 'db/lab',
  /**
   * One scenario attached to one lab, ordered. This is the tier that makes a scenario
   * reusable - the same row is linked by every lab that needs that section rather than copied
   * into each - so detaching here does not delete content, and the API refuses to delete a
   * scenario that is still linked.
   */
  scenarioLink: 'db/lab-scenario-link',
  /**
   * How one device is reached in one publication. Overrides the address and, optionally, the
   * port - never the device type, because what kind of box an item was written for does not
   * change between labs.
   */
  deviceAddress: 'db/lab-device-address',
  scenario: 'db/lab-scenario',
  configuration: 'db/lab-configuration',
  event: 'db/lab-event',
  /** The catalogue of configuration items, shared by every scenario that attaches one. */
  configurationItem: 'db/lab-configuration-item',
  /**
   * One item attached to one scenario. Readable and editable through generic CRUD - option
   * values and ordering are ordinary edits - but attaching and detaching go through
   * `db/lab-scenario/<id>/items/`, which expands prerequisites in one transaction.
   */
  scenarioItem: 'db/lab-scenario-item',
  /**
   * The device library. A device belongs to no scenario: every pod is built from the same
   * dCloud template, so one `ftd-1` row is shared by every section that works on it, and
   * `device_key` is unique across the whole table rather than per scenario.
   */
  device: 'db/lab-device',
  /**
   * One device attached to one scenario. The join is what makes a device reusable, the same
   * way `scenarioLink` makes a scenario reusable, and detaching here leaves the library row
   * alone. A lab's devices are whatever its scenarios attach - there is no lab-to-device
   * table, because a lab that named devices its sections did not use would be a second
   * answer to the same question.
   */
  scenarioDeviceLink: 'db/lab-scenario-device-link',
  /**
   * The two reference catalogues an item is authored against: what kind of box it is for, and
   * how each of its four specs is carried out. Both are tables rather than fixed lists because
   * the seventh device type and the sixth delivery method arrive without a release.
   */
  deviceType: 'db/lab-device-type',
  executionMethod: 'db/lab-execution-method',
  step: 'db/lab-scenario-step',
  criterion: 'db/lab-success-criterion',
  trigger: 'db/lab-scenario-trigger',
  pool: 'db/lab-session-pool',
  reportSink: 'db/lab-report-sink',
} as const;

/**
 * The data centres a configuration may be bound to. Not a lab table, but the configuration
 * editor cannot offer a choice without it, and there is no other reader in the UI.
 */
export const DCLOUD_DC_TABLE = 'db/dcloud-dc';

/**
 * The product catalogue, for pointing a device type at a Cisco product.
 *
 * Referenced by value on the lab row - `product_uid` plus a name snapshot - because the lab
 * models are mirrored wholesale into toolbox_dcloud, which has no product table to key against.
 * Which means this list is the only place the pairing can be made, and the only place a stale
 * snapshot can be corrected.
 */
export const PRODUCT_TABLE = 'db/product';

// -----------------------------------------------------------------------------
// Vocabulary
//
// Mirrored from the model constants rather than inferred from data, because several of
// these lists are the whole point of a field: a step whose `blast_radius` is not one of
// three known values is refused by both the loader and the injection validator, and a
// free-text box would let somebody author one anyway.
// -----------------------------------------------------------------------------

export type LabOption<T extends string = string> = {
  value: T;
  label: string;
  /** Shown as help text where the choice is consequential rather than cosmetic. */
  hint?: string;
};

export type LabPhase = 'bootstrap' | 'setup' | 'validate' | 'teardown';

/** Phases run in this order, and a step in a phase the run has not reached is blocked. */
export const LAB_PHASE_SEQUENCE: LabPhase[] = ['bootstrap', 'setup', 'validate', 'teardown'];

export const LAB_PHASES: LabOption<LabPhase>[] = [
  { value: 'bootstrap', label: 'Bootstrap', hint: 'Bring the pod to a known state before the learner touches it.' },
  { value: 'setup', label: 'Setup', hint: 'Configuration the lab depends on.' },
  { value: 'validate', label: 'Validate', hint: 'Checks that decide whether the work was done.' },
  { value: 'teardown', label: 'Teardown', hint: 'Runs last, whatever happened before it.' },
];

export type LabTransport = 'ssh' | 'https_api' | 'netconf' | 'restconf' | 'radkit' | 'manual';

export const LAB_TRANSPORTS: LabOption<LabTransport>[] = [
  { value: 'ssh', label: 'SSH' },
  { value: 'https_api', label: 'HTTPS API' },
  { value: 'netconf', label: 'NETCONF' },
  { value: 'restconf', label: 'RESTCONF' },
  { value: 'radkit', label: 'RADKit' },
  { value: 'manual', label: 'Manual', hint: 'No transport. A person does this and reports the result.' },
];

export type LabActionType =
  | 'command'
  | 'config'
  | 'api_call'
  | 'http_check'
  | 'script'
  | 'file_push'
  | 'wait'
  | 'manual'
  | 'port_check'
  | 'telemetry'
  | 'dns_check'
  | 'service_restart';

export const LAB_ACTION_TYPES: LabOption<LabActionType>[] = [
  { value: 'command', label: 'Command' },
  { value: 'config', label: 'Configuration' },
  { value: 'api_call', label: 'API call' },
  { value: 'http_check', label: 'HTTP check' },
  { value: 'script', label: 'Script' },
  { value: 'file_push', label: 'File push' },
  { value: 'wait', label: 'Wait' },
  { value: 'manual', label: 'Manual' },
  { value: 'port_check', label: 'Port check' },
  { value: 'telemetry', label: 'Telemetry' },
  { value: 'dns_check', label: 'DNS check' },
  { value: 'service_restart', label: 'Service restart' },
];

export type LabInsertMode = 'at_start' | 'mandatory' | 'catch_up';

export const LAB_INSERT_MODES: LabOption<LabInsertMode>[] = [
  {
    value: 'at_start',
    label: 'Part of the lab',
    hint: 'Materialized when the run opens. Not eligible for mid-session injection.',
  },
  {
    value: 'mandatory',
    label: 'Mid-session, mandatory',
    hint: 'Runs on every open run no matter how far along it is. For a simulated outage or a fix everyone needs.',
  },
  {
    value: 'catch_up',
    label: 'Mid-session, catch-up',
    hint: 'Skipped for anyone already past its anchor step, so it does not disturb progress. Needs an anchor.',
  },
];

export type LabBlastRadius = 'device' | 'pod' | 'shared';

export const LAB_BLAST_RADII: LabOption<LabBlastRadius>[] = [
  { value: 'device', label: 'One device', hint: 'Nothing outside the named device notices.' },
  { value: 'pod', label: 'The whole pod', hint: 'Other devices in the same lab are affected.' },
  {
    value: 'shared',
    label: 'Shared infrastructure',
    hint: 'Reaches beyond the pod. Needs a reason before it goes anywhere near a live session.',
  },
];

export type LabAuthority =
  | 'agent_automatic'
  | 'session_owner'
  | 'session_admin'
  | 'event_admin'
  | 'toolbox_service';

export const LAB_AUTHORITIES: LabOption<LabAuthority>[] = [
  { value: 'agent_automatic', label: 'Agent, automatically' },
  { value: 'session_owner', label: 'Session owner' },
  { value: 'session_admin', label: 'Session admin' },
  { value: 'event_admin', label: 'Event admin' },
  { value: 'toolbox_service', label: 'Toolbox service' },
];

/**
 * The precondition scopes a step may state in `requires`. The loader refuses any other key,
 * which is why this is a closed list and not a JSON free-for-all.
 */
export type LabPreconditionScope =
  | 'connectivity'
  | 'access'
  | 'permissions'
  | 'integrity'
  | 'licensing'
  | 'egress'
  | 'clock'
  | 'capacity'
  | 'convergence';

export const LAB_PRECONDITION_SCOPES: LabOption<LabPreconditionScope>[] = [
  { value: 'connectivity', label: 'Connectivity', hint: 'The device answers at all.' },
  { value: 'access', label: 'Access', hint: 'Credentials work.' },
  { value: 'permissions', label: 'Permissions', hint: 'Those credentials are allowed to do this.' },
  { value: 'integrity', label: 'Integrity', hint: 'Whether the device is expected to be clean or already compromised.' },
  { value: 'licensing', label: 'Licensing' },
  { value: 'egress', label: 'Egress', hint: 'The pod can reach the internet.' },
  { value: 'clock', label: 'Clock' },
  { value: 'capacity', label: 'Capacity' },
  { value: 'convergence', label: 'Convergence', hint: 'Routing or policy has settled.' },
];

export type LabIntegrityState = 'clean' | 'compromised_expected' | 'compromised_unexpected';

export const LAB_INTEGRITY_STATES: LabOption<LabIntegrityState>[] = [
  { value: 'clean', label: 'Clean' },
  {
    value: 'compromised_expected',
    label: 'Compromised, expected',
    hint: 'The lab put it in this state on purpose. A threat-protection exercise starts here.',
  },
  { value: 'compromised_unexpected', label: 'Compromised, unexpected' },
];

export type LabEvaluator = 'steps_passed' | 'fact_match' | 'manual' | 'external' | 'device_health';

export const LAB_EVALUATORS: LabOption<LabEvaluator>[] = [
  { value: 'steps_passed', label: 'Steps passed', hint: 'Named validation steps all succeeded.' },
  { value: 'fact_match', label: 'Fact match', hint: 'A fact a step emitted matches an assertion.' },
  { value: 'manual', label: 'Manual', hint: 'A proctor decides. Nothing recomputes this.' },
  { value: 'external', label: 'External', hint: 'The LMS or another system reports it. Nothing recomputes this.' },
  { value: 'device_health', label: 'Device health' },
];

/** Evaluators whose verdict comes from outside and is destroyed by re-deriving it. */
export const LAB_REPORTED_EVALUATORS: LabEvaluator[] = ['manual', 'external'];

export type LabTriggerType =
  | 'run_start'
  | 'elapsed'
  | 'session_elapsed'
  | 'progress'
  | 'criterion_met'
  | 'device_state'
  | 'manual';

export const LAB_TRIGGER_TYPES: LabOption<LabTriggerType>[] = [
  { value: 'run_start', label: 'When the run opens' },
  { value: 'elapsed', label: 'After N seconds of the run' },
  { value: 'session_elapsed', label: 'After N seconds of the session' },
  { value: 'progress', label: 'At a progress percentage' },
  { value: 'criterion_met', label: 'When a criterion is met' },
  { value: 'device_state', label: 'When a device reaches a state' },
  { value: 'manual', label: 'By hand only', hint: 'Never fires on its own. Somebody fires it from the run.' },
];

export type LabTriggerAction =
  | 'enable_steps'
  | 'skip_steps'
  | 'reopen_validation'
  | 'emit_event'
  | 'open_assist';

export const LAB_TRIGGER_ACTIONS: LabOption<LabTriggerAction>[] = [
  { value: 'enable_steps', label: 'Release gated steps', hint: 'The only thing that opens a gated step.' },
  { value: 'skip_steps', label: 'Skip steps' },
  { value: 'reopen_validation', label: 'Re-open validation' },
  { value: 'emit_event', label: 'Emit an event' },
  { value: 'open_assist', label: 'Open an assist case' },
];

export type LabRunState =
  | 'pending'
  | 'bootstrap'
  | 'setup'
  | 'validate'
  | 'passed'
  | 'failed'
  | 'error'
  | 'cancelled'
  | 'expired';

export type LabRunStepState =
  | 'pending'
  | 'blocked'
  | 'ready'
  | 'leased'
  | 'succeeded'
  | 'failed'
  | 'skipped'
  | 'timed_out'
  | 'manual_pending';

export type LabCriterionState = 'pending' | 'met' | 'unmet' | 'error' | 'waived';

export type LabAgentStatus = 'pending' | 'online' | 'degraded' | 'offline' | 'revoked';

export type LabAgentTransport = 'radkit' | 'in_pod_agent' | 'toolbox_worker';

export const LAB_AGENT_TRANSPORTS: LabOption<LabAgentTransport>[] = [
  { value: 'in_pod_agent', label: 'In-pod agent', hint: 'The utility VM inside the pod. Bind it to a session.' },
  { value: 'radkit', label: 'RADKit' },
  { value: 'toolbox_worker', label: 'Toolbox worker', hint: 'Runs on our side, not in a pod. May be unbound.' },
];

export type LabHealthState = 'unknown' | 'up' | 'degraded' | 'down';

export type LabAssistState =
  | 'requested'
  | 'triaging'
  | 'active'
  | 'waiting_on_user'
  | 'resolved'
  | 'closed_unresolved'
  | 'cancelled';

/** The states a case can be closed into. The open ones are refused by `resolve`. */
export const LAB_ASSIST_RESOLVE_STATES: LabOption<LabAssistState>[] = [
  { value: 'resolved', label: 'Resolved', hint: 'The problem was fixed.' },
  {
    value: 'closed_unresolved',
    label: 'Closed unresolved',
    hint: 'Closed without a fix - the pod was rebuilt, or the event ended.',
  },
  { value: 'cancelled', label: 'Cancelled', hint: 'Raised in error, or no longer needed.' },
];

export type LabAssistSeverity = 'low' | 'normal' | 'high' | 'blocking';

export const LAB_ASSIST_SEVERITIES: LabOption<LabAssistSeverity>[] = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'blocking', label: 'Blocking', hint: 'They cannot continue the lab.' },
];

export type LabAssistAccessMethod = 'none' | 'vpn' | 'jump_host' | 'console' | 'radkit' | 'in_pod_agent';

export const LAB_ASSIST_ACCESS_METHODS: LabOption<LabAssistAccessMethod>[] = [
  { value: 'vpn', label: 'VPN' },
  { value: 'jump_host', label: 'Jump host' },
  { value: 'console', label: 'Console' },
  { value: 'radkit', label: 'RADKit' },
  { value: 'in_pod_agent', label: 'In-pod agent' },
];

/**
 * Windows offered for granted access. Every grant has an end - the backend defaults to
 * `LAB_ASSIST_ACCESS_SECONDS` (two hours out of the box) and floors any request at a minute,
 * because access into somebody's running lab should end when the help does.
 */
export const LAB_ASSIST_ACCESS_DURATIONS: LabOption<string>[] = [
  { value: '1800', label: '30 minutes' },
  { value: '3600', label: '1 hour' },
  { value: '7200', label: '2 hours' },
  { value: '14400', label: '4 hours' },
];

/** The event kinds a person may author on a case. The rest are the service's own record. */
export type LabAssistEventKind = 'note' | 'diagnostic' | 'remediation' | 'escalated';

export const LAB_ASSIST_EVENT_KINDS: LabOption<LabAssistEventKind>[] = [
  { value: 'note', label: 'Note' },
  { value: 'diagnostic', label: 'Diagnostic' },
  { value: 'remediation', label: 'Remediation' },
  { value: 'escalated', label: 'Escalated' },
];

export type LabInjectionVerdict =
  | 'applied'
  | 'skipped_past'
  | 'skipped_complete'
  | 'already_present'
  | 'error';

export const LAB_INJECTION_VERDICT_LABELS: Record<LabInjectionVerdict, string> = {
  applied: 'Applied',
  skipped_past: 'Skipped - already past the anchor',
  skipped_complete: 'Skipped - run already finished',
  already_present: 'Already had it',
  error: 'Error',
};

export type LabSweepVerdict = 'ok' | 'problem' | 'unreachable' | 'not_ready' | 'error' | 'skipped';

export type LabPoolState = 'draft' | 'open' | 'closed' | 'archived';

/**
 * The template capacities dCloud publishes a lab at.
 *
 * dCloud does not let a topology ask for arbitrary resources - it publishes templates at a
 * handful of fixed sizes, and the size a lab was built at decides what a pod can hold. It
 * lives on the configuration and not on the scenario because the same lab is routinely
 * published large in a data centre with spare capacity and smaller in one without.
 */
export type LabSize = 'small' | 'medium' | 'large' | 'xlarge';

export const LAB_SIZES: LabOption<LabSize>[] = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
  { value: 'xlarge', label: 'X-Large' },
];

/**
 * What class of change a configuration item makes.
 *
 * A column on the item rather than a word in its options, because it is what an author
 * filters the catalogue by. `simulate` is separated from the rest because it is the one that
 * is not preparation - an attack, an outage, the misconfiguration a learner is meant to find -
 * and so the one that should almost never be pre-built into a template.
 */
export type LabItemOperation =
  | 'configure'
  | 'deploy'
  | 'integrate'
  | 'enable'
  | 'seed'
  | 'simulate'
  | 'restore';

export const LAB_ITEM_OPERATIONS: LabOption<LabItemOperation>[] = [
  { value: 'configure', label: 'Configure', hint: 'Change settings on something already there.' },
  { value: 'deploy', label: 'Deploy', hint: 'Bring something into existence in the pod.' },
  { value: 'integrate', label: 'Integrate', hint: 'Join two things so they work together.' },
  { value: 'enable', label: 'Enable', hint: 'Turn on a capability that is present but off.' },
  { value: 'seed', label: 'Seed data', hint: 'Put content in - users, logs, files, events.' },
  {
    value: 'simulate',
    label: 'Simulate',
    hint: 'The problem the lab is about. Pre-building one gives the answer away.',
  },
  { value: 'restore', label: 'Restore', hint: 'Put something back the way it was.' },
];

export type LabEventMode = 'single' | 'pool';

export const LAB_EVENT_MODES: LabOption<LabEventMode>[] = [
  {
    value: 'single',
    label: 'One session',
    hint: 'One pod, named directly on the event. For a single person or a demo.',
  },
  {
    value: 'pool',
    label: 'A pool of sessions',
    hint: 'Pods are stocked into a pool and handed out. For a room.',
  },
];

/**
 * The health states a device may report, as choices.
 *
 * The type has existed since the health table did; this is the list, because two places now ask
 * somebody to *pick* one rather than read one - a health criterion's `states`, and a trigger that
 * fires when a device reaches a state. Typing "down" into a JSON field is how a criterion ends up
 * asserting a state nothing ever reports.
 */
export const LAB_HEALTH_STATES: LabOption<LabHealthState>[] = [
  { value: 'up', label: 'Up' },
  { value: 'degraded', label: 'Degraded', hint: 'Answering, but not well enough to rely on.' },
  { value: 'down', label: 'Down' },
  {
    value: 'unknown',
    label: 'Unknown',
    hint: 'Nothing has reported recently. A stale report becomes this rather than staying green.',
  },
];

/** The criterion states a trigger may wait for. `waived` is deliberately offered: it fires too. */
export const LAB_CRITERION_STATES: LabOption<LabCriterionState>[] = [
  { value: 'met', label: 'Met' },
  { value: 'unmet', label: 'Unmet' },
  { value: 'pending', label: 'Pending', hint: 'Not yet decided either way.' },
  { value: 'error', label: 'Error', hint: 'The criterion could not be evaluated at all.' },
  { value: 'waived', label: 'Waived', hint: 'A proctor set it aside.' },
];

/**
 * The operators a fact assertion may use.
 *
 * A fixed table on the backend rather than an expression language, so that a row in the criteria
 * table can never become code running in the evaluator. That makes it a closed list here, and the
 * list is the whole of it - an exotic check needs a step to compute it and emit a fact, which is
 * the better place for it anyway because the step's output is then evidence.
 */
export type LabFactOperator =
  | 'eq'
  | 'ne'
  | 'contains'
  | 'in'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'truthy'
  | 'exists';

export const LAB_FACT_OPERATORS: LabOption<LabFactOperator>[] = [
  { value: 'eq', label: 'equals' },
  { value: 'ne', label: 'does not equal' },
  { value: 'contains', label: 'contains', hint: 'The fact is a string or a list holding the value.' },
  { value: 'in', label: 'is one of', hint: 'The value is the list; the fact has to be in it.' },
  { value: 'gt', label: 'is greater than' },
  { value: 'gte', label: 'is at least' },
  { value: 'lt', label: 'is less than' },
  { value: 'lte', label: 'is at most' },
  {
    value: 'truthy',
    label: 'is set to anything truthy',
    hint: 'No value needed. Beware: 0 and an empty string are not truthy.',
  },
  { value: 'exists', label: 'exists at all', hint: 'No value needed. A stored null still counts as absent.' },
];

/** The run metrics a progress trigger may be measured against. Attributes of the run row. */
export type LabProgressMetric =
  | 'progress_percent'
  | 'score_percent'
  | 'criteria_met'
  | 'steps_completed';

export const LAB_PROGRESS_METRICS: LabOption<LabProgressMetric>[] = [
  { value: 'progress_percent', label: 'Progress percent', hint: 'How far through the steps they are.' },
  { value: 'score_percent', label: 'Score percent', hint: 'How much of the weighted score is met.' },
  { value: 'criteria_met', label: 'Criteria met', hint: 'A count, not a percentage.' },
  { value: 'steps_completed', label: 'Steps completed', hint: 'A count, not a percentage.' },
];

export type LabEventState = 'draft' | 'scheduled' | 'running' | 'complete' | 'cancelled';

export const LAB_EVENT_STATES: LabOption<LabEventState>[] = [
  { value: 'draft', label: 'Draft', hint: 'Being put together. Nothing is handed out.' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'running', label: 'Running' },
  { value: 'complete', label: 'Complete' },
  { value: 'cancelled', label: 'Cancelled' },
];

// -----------------------------------------------------------------------------
// Row shapes
//
// Fields the UI reads, not every column. `[key: string]: any` on the authored rows because
// the editors round-trip whatever the generic CRUD API returned - dropping unlisted fields
// on a PATCH would quietly blank columns this UI does not know about yet.
// -----------------------------------------------------------------------------

/**
 * How a piece of work is carried out.
 *
 * A row rather than a choice in code, so `terraform` can be added next week. What keeps that
 * safe is `action_type`: every method resolves to one of the twelve actions the dCloud agent
 * already implements - terraform and ansible are `script`, powershell is `command` - so adding
 * a method never means shipping a new runner into a pod. A method the agent could not express
 * is refused when it is authored, not discovered when a lab runs.
 */
export type LabExecutionMethod = {
  id: number;
  /** Stable handle. Items and device types refer to it. */
  key: string;
  name: string;
  description: string;
  /** The step action this becomes. The reason a new method needs no agent change. */
  action_type: LabActionType;
  /** Read-only label, so a list can say "script step" without its own copy of the list. */
  action_type_label?: string;
  /** What it needs to travel over. Null takes the device's own. */
  transport: LabTransport | null;
  /** Delivery shape merged *under* the item's payload: interpreter, working directory. */
  payload_template: Record<string, any> | null;
  expect_template: Record<string, any> | null;
  /** Per method, because a REST call and a terraform apply cannot share one. */
  default_timeout_seconds: number;
  /**
   * False for methods that can only make a change, never observe one. Such a method used for
   * validation, health or completion scores the tool's own exit code as the lab's outcome, and
   * the compile says so.
   */
  supports_check: boolean;
  order: number;
  /** Disabled methods stay referenced and stop compiling. Retirement without deletion. */
  enabled: boolean;
  metadata: Record<string, any> | null;
  /** Read-only: how many items would change if this were edited, across all four roles. */
  item_count?: number;
  [key: string]: any;
};

/**
 * A kind of device an item can be written for.
 *
 * Open-ended on purpose - Cisco products, Windows, the Linux variants, vCenter, and whatever
 * is next. It does two things: it stops an item written for a firewall being attached to a
 * Windows host, and it says which methods can reach a box of this kind.
 *
 * The Cisco product is referenced by value, `product_uid` plus a name snapshot, with no foreign
 * key. The lab models are copied wholesale into toolbox_dcloud, which has no product table, so
 * a key would not survive the mirror. The uid is what is authoritative; the name is for reading
 * and may be stale.
 */
export type LabDeviceType = {
  id: number;
  key: string;
  name: string;
  description: string;
  /** `db/product` id, by value. Null for the types that are not a Cisco product. */
  product_uid: number | null;
  product_name: string;
  /**
   * True when this type is a *family* rather than one product - "Cisco appliance" covers
   * FMC, FTD and ISE, and each has its own API - so a device of this type has to say which
   * product it is. The type's own `product_uid` is the other arrangement: one type, one
   * product, named once here for every device that claims it.
   */
  requires_product: boolean;
  default_transport: LabTransport;
  default_port: number | null;
  /** Method ids this kind of box can be reached by. Empty means unconstrained. */
  methods: number[];
  /** Read-only, flattened by the serializer: the same methods as keys, for reading. */
  method_keys?: string[];
  order: number;
  enabled: boolean;
  metadata: Record<string, any> | null;
  /** Read-only counts: how much is written for this type, and how many devices claim it. */
  item_count?: number;
  device_count?: number;
  [key: string]: any;
};

/**
 * One device attached to one scenario.
 *
 * Read from both ends, which is why both are named on it: a section's editor asks "what boxes
 * does this work on", and a device's editor asks "which sections would I break by changing
 * this". `enabled` false keeps the attachment and stops it compiling - retirement without
 * detaching, the same shape `LabScenarioLink` uses one tier up.
 */
export type LabScenarioDeviceLink = {
  id: number;
  scenario: number;
  device: number;
  enabled: boolean;
  notes: string;
  /** Read-only, flattened by the serializer so either side renders without a second fetch. */
  scenario_name?: string;
  scenario_slug?: string;
  scenario_enabled?: boolean;
  device_key?: string;
  device_name?: string;
  device_type_key?: string;
  [key: string]: any;
};

/**
 * One box in the library.
 *
 * Not owned by a scenario. Every pod is built from the same dCloud template, so `ftd-1` is one
 * appliance that many sections work on, and attaching it is a `LabScenarioDeviceLink` rather
 * than a copy. Which is why `device_key` is unique across the whole library and not per
 * scenario: item specs, steps and the agent's work order all name a device by that string, and
 * two boxes answering to one key would make those references ambiguous.
 *
 * Editing a device edits it for every section attached to it. `scenario_links` is how a screen
 * says so before the save rather than after.
 */
export type LabDevice = {
  id: number;
  device_key: string;
  display_name: string;
  /**
   * Kind of device, from the catalogue. It decides which items may be attached here. Null
   * leaves attachments unchecked, which is allowed and is how a pod device nobody has
   * classified yet still works.
   */
  device_type: number | null;
  /** Read-only, flattened by the serializer so a device row renders without a second fetch. */
  device_type_key?: string;
  device_type_name?: string;
  device_type_product_name?: string;
  /**
   * Read-only: whether the chosen type is a family, and so whether `product_uid` below had to
   * be answered. The form gates the product picker on this.
   */
  device_type_requires_product?: boolean;
  /**
   * Which Cisco product this box is, when the type is a family that covers several. By value -
   * `db/product` id plus a name snapshot - for the same reason the type carries it that way:
   * the lab models are mirrored into toolbox_dcloud, which has no product table. Null for a
   * type that is a product in its own right, or is not a Cisco product at all.
   */
  product_uid: number | null;
  product_name: string;
  /** Read-only: every section this box is attached to. Empty is valid - a library row is
   * allowed to exist before anything uses it. */
  scenario_links?: LabScenarioDeviceLink[];
  /** Free text under the type: the type says "Windows Server", this says "2022, DC". */
  platform: string;
  role: string;
  transport: LabTransport;
  address_template: string;
  port: number | null;
  /** Names a credential. Never a credential. */
  credential_ref: string;
  jump_host: number | null;
  verify_tls: boolean;
  bootstrap_expected: boolean;
  required: boolean;
  monitored: boolean;
  order: number;
  attributes: Record<string, any> | null;
  [key: string]: any;
};

export type LabScenarioStep = {
  id: number;
  scenario: number;
  device: number | null;
  key: string;
  name: string;
  description: string;
  phase: LabPhase;
  order: number;
  action_type: LabActionType;
  payload: Record<string, any> | null;
  expect: Record<string, any> | null;
  emits_facts: Record<string, any> | null;
  timeout_seconds: number;
  retry_limit: number;
  recurring_seconds: number | null;
  optional: boolean;
  depends_on: number[];
  gated: boolean;
  insert_mode: LabInsertMode;
  skip_if_past: number | null;
  requires: Record<string, any> | null;
  /** No default in the schema. Null means nobody has said, and nothing will run it. */
  disruptive: boolean | null;
  /** Same. Both are refused unset by the loader and by injection. */
  blast_radius: LabBlastRadius | null;
  idempotent: boolean;
  exclusive: boolean;
  destructive: boolean;
  sensitive_output: boolean;
  authority: LabAuthority;
  enabled: boolean;
  [key: string]: any;
};

export type LabSuccessCriterion = {
  id: number;
  scenario: number;
  key: string;
  name: string;
  description: string;
  order: number;
  weight: number;
  required: boolean;
  evaluator: LabEvaluator;
  steps: number[];
  fact_assertion: Record<string, any> | null;
  health_assertion: Record<string, any> | null;
  hold_seconds: number;
  [key: string]: any;
};

export type LabScenarioTrigger = {
  id: number;
  scenario: number;
  key: string;
  name: string;
  description: string;
  order: number;
  trigger_type: LabTriggerType;
  condition: Record<string, any> | null;
  hold_seconds: number;
  action: LabTriggerAction;
  target_steps: number[];
  target_step_keys?: string[];
  event_name: string;
  action_detail: Record<string, any> | null;
  fire_once: boolean;
  cooldown_seconds: number;
  enabled: boolean;
  [key: string]: any;
};

/**
 * One unit of lab configuration, reusable across scenarios.
 *
 * The catalogue row. An item is a single thing that gets done to a pod, carrying everything
 * that unit needs to be trusted: how it is performed, how you know it worked, what it means
 * for it to stay working, and what counts as having completed it. Attaching an item to a
 * scenario attaches its whole prerequisite chain with it.
 *
 * Nothing here executes. These compile into steps and success criteria, which is what an
 * agent leases - see `LabScenarioItem` and the compile action.
 */
export type LabConfigurationItem = {
  id: number;
  /** Stable handle. Generated step keys are built from it, so it is not free text. */
  key: string;
  name: string;
  description: string;
  operation: LabItemOperation;
  /**
   * Kind of box this is written for. Attaching it to a device of another type is refused, and
   * a device type that enumerates its methods also refuses an item using a method it cannot
   * deliver. Null means unchecked.
   */
  device_type: number | null;
  device_type_key?: string;
  device_type_name?: string;
  /**
   * Which product inside that family, for a type that is one - see `LabDeviceType.requires_product`.
   *
   * By value, an id into `db.Product` with a name snapshot beside it and no foreign key, the same way
   * a device carries its own. Compared with the device's at attach time: FMC and FTD are both Cisco
   * appliances and share no management API, so an item written for one is refused on the other. Null
   * means any product of the type above fits, which is right for anything that is not one product's
   * API.
   */
  product_uid: number | null;
  product_name: string | null;
  /** The knobs and their defaults, flat. Overridden per scenario on the attachment. */
  options: Record<string, any> | null;
  /** The work, as the setup step it becomes: action_type, payload, expect. */
  action: Record<string, any> | null;
  /** How the work is carried out. Null falls back to the action spec's own action_type. */
  action_method: number | null;
  action_method_key?: string;
  /** How it is checked. Lands in `validate`, or in `bootstrap` when pre_configure is set. */
  validation: Record<string, any> | null;
  validation_method: number | null;
  validation_method_key?: string;
  /** Which devices this expects to stay healthy, and whether that is scored. */
  health_check: Record<string, any> | null;
  health_method: number | null;
  health_method_key?: string;
  /** The success criterion: evaluator, weight, required. Empty means steps_passed. */
  completion_criteria: Record<string, any> | null;
  completion_method: number | null;
  completion_method_key?: string;
  /**
   * True when pods arrive with this already applied: no setup step is generated and the run
   * starts past it. Asserted on the item, so it holds in every data centre the item is used
   * in - a template pre-built in one place and not another needs two items.
   */
  pre_configure: boolean;
  /** Items that must be done first. Attached automatically along with this one. */
  prerequisites: number[];
  /** Read-only, flattened by the serializer so a list can show the chain without a join. */
  prerequisite_keys?: string[];
  /** How many scenarios already use it - what decides whether an edit here is small. */
  attachment_count?: number;
  order: number;
  enabled: boolean;
  metadata: Record<string, any> | null;
  [key: string]: any;
};

/**
 * One configuration item, attached to one scenario, on one device.
 *
 * An ordering and a set of option values over the catalogue, not a copy of it - which is what
 * keeps twelve labs that all bring up a VPN from drifting apart. `added_as_prerequisite` and
 * `required_by_key` are why a row nobody asked for is there; without them the eleven items
 * that arrived with one choice look like a bug.
 *
 * **The device is part of the attachment.** The same item can be attached twice against two
 * devices - that is the normal way to configure both firewalls - and the compiled step keys
 * carry the device (`item@device`) so the two do not collide. Which is also why the item itself
 * no longer names a default device: a name matched at compile time could be wrong, a foreign
 * key here cannot.
 */
export type LabScenarioItem = {
  id: number;
  scenario: number;
  item: number;
  /** The device this item is applied to. Required - an attachment names one, always. */
  device: number;
  /** Read-only, flattened by the serializer. */
  device_key?: string;
  device_name?: string;
  /** The device's type and the item's, so a mismatch is visible from the list. */
  device_type_key?: string;
  item_device_type_key?: string;
  item_key?: string;
  item_name?: string;
  item_description?: string;
  item_operation?: LabItemOperation;
  item_pre_configure?: boolean;
  item_enabled?: boolean;
  order: number;
  /** Values for this scenario, merged over the item's defaults, one key at a time. */
  option_overrides: Record<string, any> | null;
  /** What the compile will actually substitute. Merged server-side so both agree. */
  effective_options?: Record<string, any>;
  added_as_prerequisite: boolean;
  required_by: number | null;
  required_by_key?: string;
  enabled: boolean;
  notes: string;
  [key: string]: any;
};

/**
 * One scenario attached to one lab.
 *
 * Read from both directions, which is why both ends are named on it: a lab's section list needs
 * the scenario, and a scenario's "Used In" list needs the labs. `enabled` retires a section
 * without detaching it - and `scenario_enabled` is the other way the same section can silently
 * contribute nothing, so a builder that shows one has to show both.
 */
export type LabScenarioLink = {
  id: number;
  lab: number;
  scenario: number;
  /** Read-only, flattened by the serializer. */
  lab_name?: string;
  lab_slug?: string;
  lab_enabled?: boolean;
  scenario_name?: string;
  scenario_slug?: string;
  scenario_enabled?: boolean;
  order: number;
  enabled: boolean;
  notes: string;
  [key: string]: any;
};

/**
 * How one device is reached in one publication of one lab.
 *
 * The escape hatch that makes a scenario genuinely reusable. A reused scenario keeps its devices
 * and their device types - what kind of box an item is written for does not change between labs -
 * but the address does, because each lab is published into its own topology. So this overrides
 * `address_template` and, optionally, `port`, scoped to a `LabConfiguration`, which *is* the pair
 * (lab, data centre).
 *
 * A null `port` inherits the device's rather than meaning zero. An empty `address_template` is
 * the same statement for the address.
 */
export type LabDeviceAddress = {
  id: number;
  /** The publication - lab plus data centre - this addressing applies to. */
  configuration: number;
  device: number;
  address_template: string;
  port: number | null;
  notes: string;
  /** Read-only. What is being overridden, so a redundant override is visible from the list. */
  device_key?: string;
  device_name?: string;
  device_type_key?: string;
  device_address_template?: string;
  device_port?: number | null;
  /** Where it applies: this lab, in this data centre. */
  lab_name?: string;
  data_center_name?: string;
  /**
   * Read-only: how many sections use the box this re-addresses. Not a scenario name, because
   * there is no single one to name - the device is shared, and this override applies to it in
   * every section that attaches it. A count above one is the warning worth showing; *which*
   * sections is a question about the device, answered by `LabDevice.scenario_links`.
   */
  device_scenario_count?: number;
  [key: string]: any;
};

/**
 * A lab: the top-level authored unit, and what actually gets delivered.
 *
 * It holds the top-level information and nothing executable: what it is called, who owns it, and
 * which dCloud demo it is built from. The narrative is not here - that belongs to the section it
 * is reading for, so a scenario carries the course link. What a lab *does* is compose:
 * `scenario_links` are its sections in order, and `configurations` are the data centres it is
 * published in, one dCloud topology each. The whole lab maps to one topology per data centre -
 * all of its sections run in the one pod - which is why the publication hangs here and not on
 * the individual scenarios.
 *
 * Both collections nest on the detail row, so opening a lab answers "what is in it and where
 * does it run" in one fetch. They are also the two counts the library view shows, read off the
 * arrays rather than from a separate pair of numbers that could disagree with them.
 */
export type Lab = {
  id: number;
  name: string;
  slug: string;
  description: string;
  /**
   * The dCloud demo this lab is built from. The uid is authoritative and `demo_name` is a
   * snapshot that is allowed to be stale.
   *
   * Not what a session is matched on - that is `LabConfiguration.demo_uid`, one per data centre.
   * This says which dCloud demo the lab *is*, which a publication cannot: dCloud numbers content
   * per data centre, so a lab published twice has two ids and neither is the answer on its own.
   */
  demo_uid: number | null;
  demo_name: string;
  /** Which data centre `demo_uid` was read in. A demo id means nothing without it. */
  demo_data_center: number | null;
  owner_email: string;
  enabled: boolean;
  metadata: Record<string, any> | null;
  /** The sections, in order. Present on the detail row; absent on a bare list row. */
  scenario_links?: LabScenarioLink[];
  /** Where it is published. Empty means it is authored but no session can ever match it. */
  configurations?: LabConfiguration[];
  [key: string]: any;
};

export type LabScenario = {
  id: number;
  name: string;
  slug: string;
  description: string;
  version: number;
  /**
   * The Cyber Academy course this section is reading for. The story lives there, not here: the
   * course is what tells a learner why any of the configuration matters.
   *
   * Held per section rather than per lab, so a lab of three sections can carry three pieces of
   * reading. A lab names no course at all - it names the dCloud demo it is built from.
   */
  course_uid: number | null;
  /** Display snapshot. May be stale - the uid is what is authoritative. */
  course_name: string;
  demo_uid: number | null;
  demo_name: string;
  bootstrap_only: boolean;
  auto_start: boolean;
  setup_timeout_seconds: number;
  validation_interval_seconds: number;
  pass_threshold_percent: number;
  enabled: boolean;
  /** Carries the customer. Every lab is a PseudoCo lab and the loader enforces it. */
  metadata: Record<string, any> | null;
  /**
   * The boxes this section works on, nested. Library rows reached through the link table, so
   * two sections showing the same device are showing the same row - an edit in one is an edit
   * in both. `device_links` beside it carries the attachment itself, which is what a detach
   * acts on and where `enabled` lives.
   */
  devices?: LabDevice[];
  device_links?: LabScenarioDeviceLink[];
  steps?: LabScenarioStep[];
  success_criteria?: LabSuccessCriterion[];
  /**
   * The labs this scenario is a section of. A scenario stopped owning its publications when the
   * lab tier landed - it reaches them through the labs that compose it - so this is the link
   * rows, and where it runs is a question for each `lab`.
   *
   * Also the "Used In" answer: a scenario in nine labs is nine labs' worth of consequence, and
   * this says which nine rather than only how many.
   */
  lab_links?: LabScenarioLink[];
  /**
   * Publications written before the lab tier existed, which named a scenario directly. Kept
   * separate from `lab_links` rather than blurred into it: these are a migration remnant, and a
   * scenario that has one and no lab is content nothing new should be added to.
   */
  legacy_configurations?: LabConfiguration[];
  /**
   * What the lab is made of, as opposed to `steps`, which is what those items compiled into.
   * Both are here because a compile that has fallen behind the item list is only visible when
   * you can see the two side by side.
   */
  configuration_items?: LabScenarioItem[];
  [key: string]: any;
};

/** A data centre a lab can be published in. Read from `db/dcloud-dc`, never authored here. */
export type LabDataCenter = {
  id: number;
  dcloud_dc_name: string;
  dcloud_dc_base_url: string;
  dcloud_dc_region: string;
  [key: string]: any;
};

/**
 * One lab as it is published in one data centre.
 *
 * A dCloud lab id is not global - the same lab has a different demo id in RTP and in London -
 * so the pairing of lab and data centre is the row, unique on the two together. The lab says
 * what the pod is asked to do; this says which pod, where, and how big.
 *
 * `scenario` is the pre-lab-tier shape and is nullable: rows written before labs existed named
 * a scenario directly and still match sessions. Nothing new should set it - `lab` is the live
 * binding, and the two are never both set.
 *
 * `template_topology_uid` references the dCloud template the lab was published from rather
 * than copying it. The catalogue of templates and their sizes is dCloud's, read live per data
 * centre through `dcloud/topology`, so nothing here goes stale when dCloud republishes one.
 */
export type LabConfiguration = {
  id: number;
  /** The lab this publishes. Null only on rows predating the lab tier. */
  lab: number | null;
  lab_name?: string;
  lab_slug?: string;
  /** Legacy direct binding. Null on everything authored since the lab tier. */
  scenario: number | null;
  /** Read-only, flattened by the serializer so a list does not have to join. */
  scenario_name?: string;
  scenario_slug?: string;
  /**
   * Per-device addressing for this publication, nested so the builder can show what is
   * overridden without a second fetch. Empty means every device is reached at its own address.
   */
  device_addresses?: LabDeviceAddress[];
  data_center: number;
  data_center_name?: string;
  data_center_url?: string;
  data_center_region?: string;
  name: string;
  slug: string;
  description: string;
  /** Without this, a session in this data centre is never matched to this lab. */
  demo_uid: number | null;
  demo_name: string;
  topology_uid: string;
  template_topology_uid: string;
  size: LabSize;
  enabled: boolean;
  metadata: Record<string, any> | null;
  [key: string]: any;
};

export type LabRunStep = {
  id: number;
  run: number;
  step: number;
  step_key: string;
  step_name: string;
  phase: LabPhase;
  state: LabRunStepState;
  attempts: number;
  agent: number | null;
  /** False while a gate trigger has not released it. One of three reasons for "blocked". */
  gate_released: boolean;
  gate_released_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  duration_ms: number | null;
  run_count: number;
  consecutive_failures: number;
  output: Record<string, any> | null;
  evidence: Record<string, any> | null;
  error_message: string;
  /** Which precondition scope refused, when one did. */
  blocked_scope: string;
  skip_reason: string;
};

export type LabRunCriterion = {
  id: number;
  run: number;
  criterion: number;
  criterion_key: string;
  criterion_name: string;
  required: boolean;
  weight: number;
  state: LabCriterionState;
  first_met_at: string | null;
  evaluated_at: string | null;
  detail: Record<string, any> | null;
  waived_by_email: string;
  waived_at: string | null;
  waive_reason: string;
};

export type LabRun = {
  id: number;
  session: number;
  scenario: number;
  scenario_name: string;
  scenario_slug: string;
  session_name: string;
  /** Health, assist and shares are all keyed by this, not by the session's row id. */
  session_key: string;
  attempt: number;
  state: LabRunState;
  phase: LabPhase;
  for_user_email: string;
  requested_by_email: string;
  /** How much of the work ran. */
  progress_percent: number;
  steps_total: number;
  steps_completed: number;
  steps_failed: number;
  criteria_total: number;
  criteria_met: number;
  /** How much of it was correct. A run can finish every step and still not score. */
  score_percent: number;
  started_at: string | null;
  completed_at: string | null;
  last_activity_at: string | null;
  facts: Record<string, any> | null;
  error_message: string;
  steps?: LabRunStep[];
  criteria?: LabRunCriterion[];
};

export type LabRunTrigger = {
  id: number;
  run: number;
  trigger: number;
  trigger_key: string;
  trigger_name: string;
  trigger_type: LabTriggerType;
  action: LabTriggerAction;
  state: 'armed' | 'holding' | 'fired' | 'skipped' | 'error';
  condition_since: string | null;
  fire_after: string | null;
  fired_at: string | null;
  fire_count: number;
  detail: Record<string, any> | null;
  error_message: string;
};

export type LabSessionShare = {
  id: number;
  session: number;
  shared_with_email: string;
  role: string;
  source: 'dcloud' | 'toolbox';
  active: boolean;
  invited_by_email: string;
};

export type LabSession = {
  id: number;
  session_key: string;
  name: string;
  data_center_url: string;
  demo_uid: number | null;
  demo_name: string;
  owner_email: string;
  state: 'scheduled' | 'active' | 'ended' | 'error' | 'unknown';
  scheduled_start: string | null;
  /** Not `scheduled_end_time`. `is_active` reads the state *and* this. */
  scheduled_stop: string | null;
  is_active: boolean;
  participant_emails: string[];
  launch_url: string;
  shares?: LabSessionShare[];
  lab_runs?: number[];
};

export type LabAgent = {
  id: number;
  agent_key: string;
  name: string;
  description: string;
  transport: LabAgentTransport;
  session: number | null;
  /** Read-only. Which pod this agent is in - empty when it is not bound to a session. */
  session_key: string;
  session_name: string;
  scenario: number | null;
  scenario_slug: string;
  status: LabAgentStatus;
  poll_interval_seconds: number;
  lease_seconds: number;
  last_seen_at: string | null;
  last_poll_at: string | null;
  last_error: string;
  poll_count: number;
  version: string;
  capabilities: string[] | null;
  enabled: boolean;
  telemetry: Record<string, any> | null;
  telemetry_at: string | null;
  last_round_trip_ms: number | null;
  enrolled_by_email: string;
  /** Computed. How many polls it has missed at its own interval. */
  missed_polls: number;
};

/** The enroll response. `token` exists nowhere else, ever again. */
export type LabAgentEnrollment = {
  agent: LabAgent;
  agent_key: string;
  token: string;
  token_shown_once: boolean;
  poll_url: string;
  report_url: string;
};

export type LabDeviceHealth = {
  id: number;
  session: number;
  session_key: string;
  device_key: string;
  display_name: string;
  address: string;
  state: LabHealthState;
  reachable: boolean;
  consecutive_successes: number;
  consecutive_failures: number;
  flap_count: number;
  metrics: Record<string, any> | null;
  detail: Record<string, any> | null;
  last_error: string;
  last_report_at: string | null;
  last_ok_at: string | null;
  state_since: string | null;
  monitored: boolean;
  /** Computed backend-side on purpose, so the dashboard and the sweep agree. */
  stale: boolean;
  state_duration_seconds: number | null;
};

export type LabSessionHealth = {
  session_key: string;
  summary: Record<string, any>;
  devices: LabDeviceHealth[];
};

export type LabPoolMember = {
  id: number;
  pool: number;
  session: number;
  session_key: string;
  session_state: string;
  available: boolean;
  max_assignees: number;
  label: string;
  order: number;
  reserved_until: string | null;
  reserved_for_email: string;
  assignment_count: number;
  notes: string;
};

/**
 * Live counts a proctor needs mid-event, computed per request rather than stored.
 *
 * `members_total` counts rows; `members_usable` counts the ones whose dCloud session is
 * actually live. The gap between them is a pool that looks stocked and cannot hand anything
 * out, which is worth showing rather than averaging away.
 */
export type LabPoolSnapshot = {
  pool: string;
  state: LabPoolState;
  is_open: boolean;
  members_total: number;
  members_usable: number;
  members_free: number;
  assignments_live: number;
  capacity_total: number;
};

export type LabPool = {
  id: number;
  name: string;
  slug: string;
  description: string;
  /**
   * The event this pool serves. When set, the event's configuration decides which demo, which
   * data centre and which scenario, and the three fields below are ignored - so a pool cannot
   * drift from the lab its event is delivering.
   */
  event: number | null;
  event_slug?: string | null;
  event_name?: string | null;
  scenario: number | null;
  demo_uid: number | null;
  demo_name: string;
  data_centers: string[] | null;
  state: LabPoolState;
  opens_at: string | null;
  closes_at: string | null;
  allocation_mode: 'one_per_person' | 'shared';
  claim_mode: 'self_claim' | 'proctor_assign' | 'both';
  max_assignees_per_session: number;
  reservation_seconds: number;
  auto_start_run: boolean;
  contact_email: string;
  is_open: boolean;
  members?: LabPoolMember[];
  snapshot?: LabPoolSnapshot;
  /** Present only for a steward. Everyone else gets a pool, not a list of colleagues. */
  assignments?: LabAssignment[];
  [key: string]: any;
};

/**
 * One occasion on which a lab is delivered, to one person or to a room.
 *
 * What this holds that nothing else did is *which published lab* - a configuration, and so a
 * data centre, a demo, a template and a size. `single` names the session directly; `pool`
 * owns the pools that stock it. Neither pairing is enforced, because a draft event
 * legitimately has neither yet; `is_ready` is the backend's own answer to whether it does.
 *
 * The scenario overlay is not applied by the event. The pod comes up from its dCloud
 * template, and the run that lands on it is what layers the scenario on top.
 */
export type LabEvent = {
  id: number;
  name: string;
  slug: string;
  description: string;
  configuration: number;
  /** Read-only, flattened from the configuration so a list can say which lab and where. */
  configuration_name?: string;
  configuration_slug?: string;
  scenario?: number;
  scenario_name?: string;
  scenario_slug?: string;
  data_center_name?: string;
  data_center_url?: string;
  demo_uid?: number | null;
  demo_name?: string;
  topology_uid?: string;
  template_topology_uid?: string;
  size?: LabSize;
  mode: LabEventMode;
  state: LabEventState;
  starts_at: string | null;
  ends_at: string | null;
  /** Expected attendees. Pool mode only; 0 means nobody has said. */
  seat_count: number;
  /** Single mode only. */
  session: number | null;
  session_key?: string | null;
  session_state?: string | null;
  owner_email: string;
  contact_email: string;
  auto_start_run: boolean;
  metadata: Record<string, any> | null;
  /** Computed backend-side: whether the event has the pod side it needs for its mode. */
  is_ready?: boolean;
  pools?: LabPool[];
  [key: string]: any;
};

export type LabAssignment = {
  id: number;
  pool: number | null;
  pool_slug: string | null;
  member: number | null;
  pod_label: string | null;
  session: number;
  session_key: string;
  session_state: string;
  assignee_email: string;
  display_name: string;
  team_label: string;
  state: 'active' | 'released' | 'expired' | 'revoked';
  source: 'pool' | 'self_service' | 'manual';
  run: number | null;
  run_state: string | null;
  progress_percent: number | null;
  score_percent: number | null;
  assigned_at: string | null;
  released_at: string | null;
  release_reason: string;
  access_url: string;
};

export type LabAssistEvent = {
  id: number;
  case: number;
  kind: string;
  actor_email: string;
  summary: string;
  detail: Record<string, any> | null;
  occurred_at: string;
};

export type LabAssistCase = {
  id: number;
  session: number;
  session_key: string;
  run: number | null;
  reference: string;
  opened_by_email: string;
  assigned_to_email: string;
  origin: 'user' | 'automatic' | 'sweep';
  state: LabAssistState;
  severity: LabAssistSeverity;
  subject: string;
  detail: string;
  device_key: string;
  root_cause: string;
  fix_applied: string;
  resolution: string;
  access_method: LabAssistAccessMethod;
  access_granted_by_email: string | null;
  access_granted_at: string | null;
  access_expires_at: string | null;
  access_revoked_at: string | null;
  opened_at: string;
  resolved_at: string | null;
  is_open: boolean;
  /** Whether granted access is still live, rather than granted-and-since-expired. */
  access_is_current: boolean;
  events?: LabAssistEvent[];
};

export type LabSweepResult = {
  id: number;
  sweep: number;
  session_key: string;
  session_name: string;
  verdict: LabSweepVerdict;
  problems: string[] | null;
  devices_total: number;
  devices_up: number;
  devices_down: number;
  devices_stale: number;
  agents_online: number;
  run_state: string;
  detail: Record<string, any> | null;
};

export type LabSweep = {
  id: number;
  label: string;
  pool: number | null;
  scenario: number | null;
  state: 'running' | 'completed' | 'partial' | 'failed';
  initiated_by_email: string;
  started_at: string | null;
  finished_at: string | null;
  sessions_seen: number;
  sessions_ok: number;
  sessions_problem: number;
  sessions_error: number;
  summary: Record<string, any> | null;
  error_message: string;
  results?: LabSweepResult[];
};

export type LabInjectionResult = {
  id: number;
  injection: number;
  run: number;
  session_key: string;
  verdict: LabInjectionVerdict;
  detail: string;
};

export type LabInjection = {
  id: number;
  step: number;
  step_key: string;
  step_name: string;
  insert_mode: LabInsertMode;
  scenario: number;
  scenario_slug: string;
  pool: number | null;
  pool_slug: string | null;
  reason: string;
  state: 'pending' | 'applying' | 'completed' | 'partial' | 'failed';
  initiated_by_email: string;
  started_at: string | null;
  finished_at: string | null;
  runs_seen: number;
  runs_applied: number;
  runs_skipped: number;
  runs_error: number;
  error_message: string;
  results?: LabInjectionResult[];
};

/** What a dry run answers: which pods this would reach, before it reaches any of them. */
export type LabInjectionPreview = {
  dry_run: true;
  step: string;
  insert_mode: LabInsertMode;
  phase: LabPhase;
  disruptive: boolean;
  blast_radius: LabBlastRadius;
  runs: { run_id: number; session_key: string; state: LabRunState; phase: LabPhase }[];
};

// -----------------------------------------------------------------------------
// Operations - the lab controller
// -----------------------------------------------------------------------------

export const fetchLabSessions = (activeOnly = false): Promise<LabSession[]> =>
  labFetch<LabSession[]>('GET', 'sessions/', {
    params: activeOnly ? { active: 'true' } : {},
  });

export const shareLabSession = (
  sessionKey: string,
  emails: string[],
): Promise<{ session: LabSession; recorded: string[] }> =>
  labFetch('POST', `sessions/${encodeURIComponent(sessionKey)}/shares/`, { body: { emails } });

export const fetchLabSessionHealth = (sessionKey: string): Promise<LabSessionHealth> =>
  labFetch('GET', `sessions/${encodeURIComponent(sessionKey)}/health/`);

export type LabRunFilters = {
  session_key?: string;
  scenario?: string;
  state?: LabRunState;
  open?: boolean;
};

export const fetchLabRuns = (filters: LabRunFilters = {}): Promise<LabRun[]> =>
  labFetch<LabRun[]>('GET', 'runs/', {
    params: {
      ...(filters.session_key ? { session_key: filters.session_key } : {}),
      ...(filters.scenario ? { scenario: filters.scenario } : {}),
      ...(filters.state ? { state: filters.state } : {}),
      ...(filters.open ? { open: 'true' } : {}),
    },
  });

export const fetchLabRun = (runId: number): Promise<LabRun> => labFetch('GET', `runs/${runId}/`);

export const startLabRun = (payload: {
  session_key: string;
  scenario: string;
  for_user_email?: string;
}): Promise<LabRun> => labFetch('POST', 'runs/', { body: payload });

/**
 * Recompute now. `revalidate` also re-opens the validation phase, which makes the checks
 * run again against the pod as it is - the right thing after somebody fixed something, and
 * the wrong thing to do casually, because it resets the validation steps' results.
 */
export const evaluateLabRun = (runId: number, revalidate = false): Promise<LabRun> =>
  labFetch('POST', `runs/${runId}/`, { body: { revalidate } });

export const reportLabCriterion = (
  runId: number,
  criterionKey: string,
  payload: { state?: LabCriterionState; waive?: boolean; reason?: string; detail?: Record<string, any> },
): Promise<LabRun> =>
  labFetch('POST', `runs/${runId}/criteria/${encodeURIComponent(criterionKey)}/`, { body: payload });

export const fetchLabRunTriggers = (runId: number): Promise<LabRunTrigger[]> =>
  labFetch('GET', `runs/${runId}/triggers/`);

export const fireLabTrigger = (runId: number, triggerKey: string): Promise<LabRunTrigger> =>
  labFetch('POST', `runs/${runId}/triggers/`, { body: { trigger: triggerKey } });

export const fetchLabAgents = (): Promise<LabAgent[]> => labFetch('GET', 'agents/');

export const enrollLabAgent = (payload: {
  name: string;
  transport: LabAgentTransport;
  session_key?: string;
  scenario?: string;
  poll_interval_seconds?: number;
  lease_seconds?: number;
  capabilities?: string[];
}): Promise<LabAgentEnrollment> => labFetch('POST', 'agents/', { body: payload });

export const revokeLabAgent = (agentKey: string, reason = ''): Promise<LabAgent> =>
  labFetch('POST', `agents/${encodeURIComponent(agentKey)}/revoke/`, { body: { reason } });

export const fetchLabPools = (includeAll = false): Promise<LabPool[]> =>
  labFetch<LabPool[]>('GET', 'pools/', { params: includeAll ? { all: 'true' } : {} });

export const fetchLabPool = (slug: string): Promise<LabPool> =>
  labFetch('GET', `pools/${encodeURIComponent(slug)}/`);

/** Mirrors matching dCloud sessions in as pods. It never creates a session. */
export const stockLabPool = (
  slug: string,
): Promise<{ added: number; already_present: number; snapshot: LabPoolSnapshot }> =>
  labFetch('POST', `pools/${encodeURIComponent(slug)}/stock/`, { body: {} });

/** Always for the caller. The backend takes the assignee from the token, not the body. */
export const claimLabPod = (
  slug: string,
  payload: { display_name?: string; team_label?: string } = {},
): Promise<LabAssignment> =>
  labFetch('POST', `pools/${encodeURIComponent(slug)}/claim/`, { body: payload });

export const assignLabPod = (
  slug: string,
  payload: { session_key: string; email: string; display_name?: string; team_label?: string },
): Promise<LabAssignment> =>
  labFetch('POST', `pools/${encodeURIComponent(slug)}/assign/`, { body: payload });

export const startSelfServiceLab = (payload: {
  session_key: string;
  display_name?: string;
}): Promise<LabAssignment> => labFetch('POST', 'self-service/', { body: payload });

export const fetchLabAssignments = (liveOnly = false): Promise<LabAssignment[]> =>
  labFetch<LabAssignment[]>('GET', 'assignments/', { params: liveOnly ? { live: 'true' } : {} });

export const releaseLabAssignment = (assignmentId: number, reason = ''): Promise<LabAssignment> =>
  labFetch('POST', `assignments/${assignmentId}/release/`, { body: { reason } });

export const fetchLabAssistCases = (queue = false): Promise<LabAssistCase[]> =>
  labFetch<LabAssistCase[]>('GET', 'assist/', { params: queue ? { queue: 'true' } : {} });

export const fetchLabAssistCase = (reference: string): Promise<LabAssistCase> =>
  labFetch('GET', `assist/${encodeURIComponent(reference)}/`);

export const openLabAssistCase = (payload: {
  session_key: string;
  subject: string;
  detail?: string;
  severity?: LabAssistSeverity;
  device_key?: string;
  run_id?: number;
}): Promise<LabAssistCase> => labFetch('POST', 'assist/', { body: payload });

/** The five actions the case endpoint takes. Anything else is refused. */
export type LabAssistAction =
  | { action: 'assign'; email?: string }
  | { action: 'note'; summary: string; kind?: LabAssistEventKind; detail?: Record<string, any> }
  | { action: 'grant_access'; method: LabAssistAccessMethod; duration_seconds?: number }
  | { action: 'revoke_access'; reason?: string }
  | { action: 'resolve'; state?: LabAssistState; resolution?: string; root_cause?: string; fix_applied?: string };

export const actOnLabAssistCase = (
  reference: string,
  action: LabAssistAction,
): Promise<LabAssistCase> =>
  labFetch('POST', `assist/${encodeURIComponent(reference)}/`, { body: action });

export const fetchLabSweeps = (limit = 20): Promise<LabSweep[]> =>
  labFetch<LabSweep[]>('GET', 'sweeps/', { params: { limit } });

export type LabInjectionFilters = { scenario?: string; pool?: string; step?: string };

export const fetchLabInjections = (filters: LabInjectionFilters = {}): Promise<LabInjection[]> =>
  labFetch<LabInjection[]>('GET', 'injections/', { params: { ...filters } });

export const fetchLabInjection = (injectionId: number): Promise<LabInjection> =>
  labFetch('GET', `injections/${injectionId}/`);

export const fetchLabInjectionResults = (
  injectionId: number,
  verdict?: LabInjectionVerdict,
): Promise<LabInjectionResult[]> =>
  labFetch<LabInjectionResult[]>('GET', `injections/${injectionId}/results/`, {
    params: verdict ? { verdict } : {},
  });

export type LabInjectionRequest = {
  step_id?: number;
  scenario?: string;
  step?: string;
  pool?: string;
  reason?: string;
};

/** See which runs would be reached, without touching any of them. Do this first. */
export const previewLabInjection = (payload: LabInjectionRequest): Promise<LabInjectionPreview> =>
  labFetch('POST', 'injections/', { body: { ...payload, dry_run: true } });

export const injectLabStep = (payload: LabInjectionRequest): Promise<LabInjection> =>
  labFetch('POST', 'injections/', { body: payload });

export const reapplyLabInjection = (injectionId: number): Promise<LabInjection> =>
  labFetch('POST', `injections/${injectionId}/apply/`, { body: {} });

// -----------------------------------------------------------------------------
// Authoring - toolbox_db generic CRUD
// -----------------------------------------------------------------------------

export const fetchLabs = (params: Record<string, any> = {}): Promise<Lab[]> =>
  fetchItems(LAB_TABLES.lab, params);

/** The detail row nests the sections and the publications. Both are what a lab *is*. */
export const fetchLab = (id: number | string): Promise<Lab> =>
  fetchItem(`${LAB_TABLES.lab}/${id}`);

/**
 * Link rows on their own, for the direction the nesting does not serve.
 *
 * A lab's sections arrive nested on the lab, and a scenario's labs arrive nested on the
 * scenario, so this is for the third question: reordering, where the rows are edited as rows.
 */
export const fetchLabScenarioLinks = (
  params: Record<string, any> = {},
): Promise<LabScenarioLink[]> => fetchItems(LAB_TABLES.scenarioLink, params);

/**
 * Address overrides for one publication.
 *
 * Also nested on the configuration. Fetched apart when the override editor is open on its own,
 * because that screen has to show every device in every section of the lab - including the ones
 * with no override row yet, which by definition are not in this list.
 */
export const fetchLabDeviceAddresses = (
  params: Record<string, any> = {},
): Promise<LabDeviceAddress[]> => fetchItems(LAB_TABLES.deviceAddress, params);

/**
 * The device library.
 *
 * Devices also arrive nested on their scenario, which is how the builder reads them. This is the
 * other view of the same rows: the library list, where the question is "what boxes does the
 * estate have and which scenarios use them" rather than "what is in this section".
 *
 * Note what cannot be asked here: `?scenario=<id>`. Neither generic engine crosses a join table
 * - django_filters skips m2m fields and the Go filter engine handles plain columns - and an
 * unrecognised parameter is *ignored* rather than refused, so that call would quietly return the
 * whole library. One section's devices come from the scenario detail row, nested.
 */
export const fetchLabDevices = (
  params: Record<string, any> = {},
): Promise<LabDevice[]> => fetchItems(LAB_TABLES.device, params);

/**
 * Attachment rows on their own, for the direction the nesting does not serve.
 *
 * A section's devices arrive nested on the scenario and a device's sections arrive nested on the
 * device, so this is for editing the attachment itself - detaching one, or disabling it without
 * detaching.
 */
export const fetchLabScenarioDeviceLinks = (
  params: Record<string, any> = {},
): Promise<LabScenarioDeviceLink[]> => fetchItems(LAB_TABLES.scenarioDeviceLink, params);

export const fetchLabScenarios = (params: Record<string, any> = {}): Promise<LabScenario[]> =>
  fetchItems(LAB_TABLES.scenario, params);

/** The detail row nests devices, steps and success criteria. Triggers are fetched apart. */
export const fetchLabScenario = (id: number | string): Promise<LabScenario> =>
  fetchItem(`${LAB_TABLES.scenario}/${id}`);

export const fetchLabScenarioTriggers = (scenarioId: number): Promise<LabScenarioTrigger[]> =>
  fetchItems(LAB_TABLES.trigger, { scenario: scenarioId });

export const fetchLabConfigurations = (
  params: Record<string, any> = {},
): Promise<LabConfiguration[]> => fetchItems(LAB_TABLES.configuration, params);

export const fetchLabConfiguration = (id: number | string): Promise<LabConfiguration> =>
  fetchItem(`${LAB_TABLES.configuration}/${id}`);

/** The whole catalogue. Small enough to hold, and every attach screen needs all of it. */
export const fetchLabConfigurationItems = (
  params: Record<string, any> = {},
): Promise<LabConfigurationItem[]> => fetchItems(LAB_TABLES.configurationItem, params);

export const fetchLabConfigurationItem = (id: number | string): Promise<LabConfigurationItem> =>
  fetchItem(`${LAB_TABLES.configurationItem}/${id}`);

/**
 * The two reference catalogues, whole.
 *
 * Both are small - a handful of rows each - and every item form, device form and attach screen
 * needs all of them to render a select. Fetching them filtered would mean a round trip per
 * dropdown and a screen that cannot name the type an item was written for.
 */
export const fetchLabDeviceTypes = (
  params: Record<string, any> = {},
): Promise<LabDeviceType[]> => fetchItems(LAB_TABLES.deviceType, params);

export const fetchLabExecutionMethods = (
  params: Record<string, any> = {},
): Promise<LabExecutionMethod[]> => fetchItems(LAB_TABLES.executionMethod, params);

/** Cisco products, for pointing a device type at one. Filtered the way the RBAC editor does. */
export const fetchLabProducts = (): Promise<{ id: number; name: string }[]> =>
  fetchItems(PRODUCT_TABLE, { vendor__is_cisco: true, page: 1, size: 2000 });

/** One scenario's attached items. Also nested on the scenario detail row. */
export const fetchLabScenarioItems = (scenarioId: number): Promise<LabScenarioItem[]> =>
  fetchItems(LAB_TABLES.scenarioItem, { scenario: scenarioId });

/**
 * Every attachment, across every scenario.
 *
 * The other direction through the same table. `fetchLabScenarioItems` answers "what is in this
 * section", which is the builder's question; this answers "where is this item used", which is the
 * library's - and that cannot be assembled from a per-scenario fetch without one request per
 * scenario. The item row only carries `attachment_count`, a number with no way to see the list, and
 * a count is not enough to decide whether editing a shared item is safe.
 */
export const fetchLabAttachments = (
  params: Record<string, any> = {},
): Promise<LabScenarioItem[]> => fetchItems(LAB_TABLES.scenarioItem, params);

/**
 * What an attach or a compile reports back.
 *
 * Warnings are the part worth reading. Every one of them describes something that *will* run -
 * a step excluded from fleet sweeps because it does not state its blast radius, an option
 * token left unresolved, a device named that the scenario does not have - so they are returned
 * rather than raised. Anything that would produce work nobody can act on fails the request
 * instead, and rolls the whole compile back.
 */
export type LabItemAttachResult = {
  attached: string[];
  added_as_prerequisites: string[];
  promoted: string[];
  total: number;
  items: LabScenarioItem[];
  compile_required: boolean;
};

export type LabItemDetachResult = {
  detached: string[];
  /** Prerequisites dropped because nothing left in the scenario needed them. */
  pruned: string[];
  items: LabScenarioItem[];
  compile_required: boolean;
};

export type LabCompileResult = {
  scenario: string;
  items: number;
  steps_created: string[];
  steps_updated: number;
  /** Kept but switched off, because a run has already been scored against them. */
  steps_disabled: string[];
  steps_deleted: string[];
  criteria_created: string[];
  criteria_updated: number;
  criteria_disabled: string[];
  criteria_deleted: string[];
  monitored_devices: string[];
  warnings: string[];
};

/** One item on one device. The unit the attach endpoint takes. */
export type LabItemAttachPair = {
  item: number | string;
  device: number | string;
};

/**
 * Attach items to devices in a scenario, with every prerequisite they imply.
 *
 * One request rather than a POST per row: a dropped connection halfway through a client-side
 * loop leaves a scenario holding a dependent whose prerequisite never arrived, which compiles
 * into a run that blocks forever on a dependency that does not exist.
 *
 * Every pair names a device, because an attachment is "this item, on this box" and nothing
 * downstream can guess the second half. Prerequisites pulled in alongside inherit the device
 * where their type allows it, and land on the scenario's only device of the right type where it
 * does not - if there are two of those, the endpoint refuses and says to attach it explicitly
 * first, since picking one for the author configures the wrong firewall.
 *
 * `optionOverrides` is keyed by item key and applied only to the items named directly - a
 * prerequisite arrives with the item's own defaults, because nobody chose it.
 */
export const attachLabScenarioItems = (
  scenarioId: number,
  items: LabItemAttachPair[],
  optionOverrides: Record<string, Record<string, any>> = {},
): Promise<LabItemAttachResult> =>
  postItem(`${LAB_TABLES.scenario}/${scenarioId}/items`, {
    items,
    option_overrides: optionOverrides,
  });

/**
 * Detach one item, and any prerequisite left behind with nothing to serve.
 *
 * Not `deleteLabRow`: that goes to `db/lab-scenario-item/<id>/` and would remove the one row
 * named, leaving the prerequisites it dragged in behind as leftovers nobody can tell from
 * deliberate choices. It also returns a bare boolean, and the list of what was pruned is the
 * reason to call this at all - hence the request here rather than through `deleteItem`.
 */
export const detachLabScenarioItem = async (
  scenarioId: number,
  attachmentId: number,
): Promise<LabItemDetachResult> => {
  const path = `${LAB_TABLES.scenario}/${scenarioId}/items/${attachmentId}/`;

  // Headers through `getApiHeaders`, not a bare fetch: it resolves the toolbox JWT from redux
  // or the `tb_jwt` cookie, and without it a relative /api path is unauthenticated in local
  // development while looking fine in a deployment.
  const response = await fetch(`${process.env.REACT_APP_API}/${path}`, {
    method: 'DELETE',
    headers: getApiHeaders(null, 'DELETE', path),
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(response.statusText);
  }

  return response.json();
};

/**
 * Compile the scenario's items into steps and success criteria.
 *
 * Idempotent - compiling twice with nothing changed rewrites the same rows to the same values -
 * which is what makes it safe to offer as a button. Rows the compile would no longer write are
 * deleted when no run has used them and disabled when one has.
 */
export const compileLabScenario = (scenarioId: number): Promise<LabCompileResult> =>
  postItem(`${LAB_TABLES.scenario}/${scenarioId}/compile`, {});

/** Events nest their pools, the way a run nests its steps: in pool mode the pools are the event. */
export const fetchLabEvents = (params: Record<string, any> = {}): Promise<LabEvent[]> =>
  fetchItems(LAB_TABLES.event, params);

export const fetchLabEvent = (id: number | string): Promise<LabEvent> =>
  fetchItem(`${LAB_TABLES.event}/${id}`);

/** The data centres a configuration may be bound to. Reference data, not lab content. */
export const fetchLabDataCenters = (): Promise<LabDataCenter[]> => fetchItems(DCLOUD_DC_TABLE, {});

export const saveLabRow = <T extends { id?: number }>(table: string, row: T): Promise<T> =>
  row.id ? patchItem(table, row as any) : postItem(table, row);

/** The trailing slash is ours to add - `deleteItem` does not force one, and DRF wants it. */
export const deleteLabRow = (table: string, id: number): Promise<boolean> =>
  deleteItem(`${table}/${id}/`);

// -----------------------------------------------------------------------------
// Reading the state
// -----------------------------------------------------------------------------

const labelFor = <T extends string>(options: LabOption<T>[], value?: T | null): string =>
  options.find((option) => option.value === value)?.label || String(value || '—');

export const labPhaseLabel = (phase?: LabPhase | null) => labelFor(LAB_PHASES, phase);
export const labActionTypeLabel = (a?: LabActionType | null) => labelFor(LAB_ACTION_TYPES, a);
export const labTransportLabel = (t?: LabTransport | null) => labelFor(LAB_TRANSPORTS, t);
export const labInsertModeLabel = (m?: LabInsertMode | null) => labelFor(LAB_INSERT_MODES, m);
export const labBlastRadiusLabel = (b?: LabBlastRadius | null) => labelFor(LAB_BLAST_RADII, b);
export const labEvaluatorLabel = (e?: LabEvaluator | null) => labelFor(LAB_EVALUATORS, e);
export const labTriggerTypeLabel = (t?: LabTriggerType | null) => labelFor(LAB_TRIGGER_TYPES, t);
export const labTriggerActionLabel = (a?: LabTriggerAction | null) => labelFor(LAB_TRIGGER_ACTIONS, a);
export const labSizeLabel = (s?: LabSize | null) => labelFor(LAB_SIZES, s);
export const labItemOperationLabel = (o?: LabItemOperation | null) =>
  labelFor(LAB_ITEM_OPERATIONS, o);
export const labEventModeLabel = (m?: LabEventMode | null) => labelFor(LAB_EVENT_MODES, m);
export const labEventStateLabel = (s?: LabEventState | null) => labelFor(LAB_EVENT_STATES, s);

/** Harbor tag sentiments. Kept in one place so a state does not read green here and red there. */
export type LabSentiment = 'info' | 'success' | 'warning' | 'danger' | 'interact';

export const labRunStateSentiment = (state?: LabRunState): LabSentiment => {
  if (state === 'passed') return 'success';
  if (state === 'failed' || state === 'error') return 'danger';
  if (state === 'expired' || state === 'cancelled') return 'warning';
  return 'info';
};

export const labStepStateSentiment = (state?: LabRunStepState): LabSentiment => {
  if (state === 'succeeded') return 'success';
  if (state === 'failed' || state === 'timed_out') return 'danger';
  if (state === 'blocked' || state === 'skipped' || state === 'manual_pending') return 'warning';
  return 'info';
};

export const labCriterionStateSentiment = (state?: LabCriterionState): LabSentiment => {
  if (state === 'met') return 'success';
  if (state === 'unmet' || state === 'error') return 'danger';
  if (state === 'waived') return 'warning';
  return 'info';
};

export const labHealthSentiment = (state?: LabHealthState): LabSentiment => {
  if (state === 'up') return 'success';
  if (state === 'down') return 'danger';
  if (state === 'degraded') return 'warning';
  return 'info';
};

export const labAgentSentiment = (status?: LabAgentStatus): LabSentiment => {
  if (status === 'online') return 'success';
  if (status === 'offline' || status === 'revoked') return 'danger';
  if (status === 'degraded') return 'warning';
  return 'info';
};

export const labAssistSentiment = (severity?: LabAssistSeverity): LabSentiment => {
  if (severity === 'blocking') return 'danger';
  if (severity === 'high') return 'warning';
  return 'info';
};

export const labSweepSentiment = (verdict?: LabSweepVerdict): LabSentiment => {
  if (verdict === 'ok') return 'success';
  if (verdict === 'problem' || verdict === 'unreachable' || verdict === 'error') return 'danger';
  return 'warning';
};

export const labEventStateSentiment = (state?: LabEventState): LabSentiment => {
  if (state === 'running') return 'success';
  if (state === 'cancelled') return 'danger';
  if (state === 'draft') return 'warning';

  return 'info';
};

export const labInjectionSentiment = (verdict?: LabInjectionVerdict): LabSentiment => {
  if (verdict === 'applied') return 'success';
  if (verdict === 'error') return 'danger';
  return 'info';
};

/**
 * Why this step is blocked - which is not one question.
 *
 * `refresh_step_readiness` has three independent gates and "blocked" is what all three
 * look like from outside: the run has not reached the step's phase, a trigger has not
 * released it, or a step it depends on has not succeeded. Telling somebody "blocked" and
 * leaving them to work out which is the difference between a usable run view and a
 * screenshot in a support case.
 */
export const labStepBlockReason = (
  runStep: LabRunStep,
  run: LabRun,
  stepsByKey: Record<string, LabRunStep> = {},
  dependencyKeys: string[] = [],
): string | null => {
  if (runStep.state !== 'blocked') return null;

  const runPhaseIndex = LAB_PHASE_SEQUENCE.indexOf(run.phase);
  const stepPhaseIndex = LAB_PHASE_SEQUENCE.indexOf(runStep.phase);

  if (stepPhaseIndex > runPhaseIndex && runPhaseIndex >= 0) {
    return `Waiting for the ${labPhaseLabel(runStep.phase).toLowerCase()} phase; the run is in ${labPhaseLabel(
      run.phase,
    ).toLowerCase()}.`;
  }

  if (!runStep.gate_released) {
    return 'Gated. A trigger has to release this before it can run.';
  }

  const unmet = dependencyKeys.filter((key) => stepsByKey[key]?.state !== 'succeeded');

  if (unmet.length) {
    return `Waiting on ${unmet.join(', ')}.`;
  }

  if (runStep.blocked_scope) {
    return `Precondition not met: ${runStep.blocked_scope}.`;
  }

  return 'Blocked. Nothing here explains why, which is worth reporting.';
};

/**
 * Whether this step may be pushed at running pods, and if not, what is missing.
 *
 * The same four rules the backend applies, checked here so the button can say why it is
 * disabled instead of the API refusing after the click. `disruptive` and `blast_radius`
 * have no default: null means nobody stated the reach, and an unstated reach is not
 * eligible to go out.
 */
export const labStepInjectionBlockers = (step: LabScenarioStep): string[] => {
  const blockers: string[] = [];

  if (step.insert_mode !== 'mandatory' && step.insert_mode !== 'catch_up') {
    blockers.push('Insert mode has to be mandatory or catch-up.');
  }

  if (step.disruptive === null || step.disruptive === undefined) {
    blockers.push('Nobody has stated whether this is disruptive.');
  }

  if (!step.blast_radius) {
    blockers.push('Nobody has stated the blast radius.');
  }

  if (step.insert_mode === 'catch_up' && !step.skip_if_past) {
    blockers.push('A catch-up step needs an anchor step to decide who is already past it.');
  }

  if (!step.enabled) {
    blockers.push('The step is disabled.');
  }

  return blockers;
};

/** Percent, but never a bar that says 100% for a run that has not been judged yet. */
export const labRunSummary = (run: LabRun) => ({
  progressLabel: `${run.steps_completed}/${run.steps_total} steps`,
  scoreLabel: `${run.criteria_met}/${run.criteria_total} criteria`,
  progressPercent: Math.max(0, Math.min(100, run.progress_percent || 0)),
  scorePercent: Math.max(0, Math.min(100, run.score_percent || 0)),
});

/** The customer a scenario is set at. Every lab is a PseudoCo lab; this shows when one is not. */
export const labScenarioCustomer = (scenario?: LabScenario | null): string =>
  String(scenario?.metadata?.customer || '');

/**
 * The short dCloud code for a data centre row, e.g. `rtp`.
 *
 * The dCloud catalogs are addressed by that code (`datacenters/rtp/inventory-demos`) and
 * `DCloudDC` does not store one - it has a display name and a base URL. Matching the five
 * known codes against both beats parsing the URL, whose shape differs between data centres.
 * An empty string means nothing matched, and a caller should then offer no catalog rather than
 * fetch a confidently wrong one.
 */
export const labDataCenterCode = (dataCenter?: LabDataCenter | null): string => {
  if (!dataCenter) return '';

  const haystack = `${dataCenter.dcloud_dc_name || ''} ${dataCenter.dcloud_dc_base_url || ''}`.toLowerCase();
  const known = DCLOUD_DATA_CENTERS.find((entry) => haystack.includes(entry.value));

  if (known) return known.value;

  const name = String(dataCenter.dcloud_dc_name || '').trim().toLowerCase();

  return /^[a-z0-9-]+$/.test(name) ? name : '';
};

/**
 * Text as a slug the API will take: `SlugField` accepts letters, numbers, hyphens and
 * underscores only.
 *
 * Slugs are generated rather than typed everywhere a row is created in the admin UI. They are
 * not a label - runs, events and injections refer to a row by its slug - so a hand-typed one is
 * a chance to introduce a spelling that something downstream already recorded differently, in
 * exchange for a field nobody wants to fill in.
 */
export const labSlugify = (value: string): string =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 255);

/**
 * The slug for one publication of a lab.
 *
 * Derived from the lab and the data centre rather than from the name, because `slug` is unique
 * across the whole table while a name is free text. Two data centres publishing a lab somebody
 * called "Ransomware lab" is ordinary; two rows with the same slug is a 400. The pair this is
 * built from is exactly the pair the table is unique on, so a collision here means a duplicate
 * the form already refuses.
 *
 * Takes a slug rather than a row so it serves both bindings: the lab's for anything authored
 * since the lab tier, and a legacy row's scenario slug unchanged.
 */
export const labConfigurationSlug = (
  ownerSlug?: string | null,
  dataCenter?: LabDataCenter | null,
): string => {
  const ownerPart = labSlugify(ownerSlug || '');
  const dcPart = labDataCenterCode(dataCenter) || labSlugify(dataCenter?.dcloud_dc_name || '');

  return [ownerPart, dcPart].filter(Boolean).join('-');
};

/**
 * dCloud's capacity label as one of ours, or null when it is something else.
 *
 * Null rather than a guess: the size decides what a pod can hold, and quietly recording
 * "medium" for a template dCloud calls something else produces a configuration that claims a
 * capacity the pod does not have.
 */
export const labSizeFromTemplateLabel = (label?: string | null): LabSize | null => {
  const normalized = String(label || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-');

  if (!normalized) return null;
  if (normalized === 'x-large' || normalized === 'xlarge' || normalized === 'extra-large') {
    return 'xlarge';
  }

  return (LAB_SIZES.find((size) => size.value === normalized)?.value as LabSize) || null;
};

/**
 * What a publication publishes, named.
 *
 * The lab, or the scenario for rows that predate the lab tier, which is the honest thing to show:
 * those rows really are bound to a scenario, and labelling one with a lab it does not have would
 * hide the only reason it behaves differently. Neither is a real state and it says so, because a
 * publication bound to nothing matches sessions and then delivers nothing.
 */
export const labConfigurationOwner = (configuration?: LabConfiguration | null): string => {
  if (!configuration) return '-';

  if (configuration.lab) {
    return configuration.lab_name || configuration.lab_slug || `lab ${configuration.lab}`;
  }

  if (configuration.scenario) {
    return (
      configuration.scenario_name ||
      configuration.scenario_slug ||
      `scenario ${configuration.scenario}`
    );
  }

  return 'nothing';
};

/** A configuration in one line: which lab, in which data centre, at what size. */
export const labConfigurationSummary = (configuration?: LabConfiguration | null): string => {
  if (!configuration) return '-';

  const owner = labConfigurationOwner(configuration);

  return [
    owner,
    configuration.data_center_name || configuration.data_center_url || `dc ${configuration.data_center}`,
    labSizeLabel(configuration.size),
  ]
    .filter(Boolean)
    .join(' • ');
};

/**
 * A lab's dCloud demo link in one line, or '' when it has none.
 *
 * The data centre is part of the line rather than a detail beside it: dCloud numbers content per
 * data centre, so "demo 12345" on its own names nothing in particular. A linked demo whose data
 * centre row has since gone says so, because the uid can no longer be checked against a catalog.
 */
export const labDemoSummary = (lab?: Lab | null, dataCenter?: LabDataCenter | null): string => {
  if (!lab || lab.demo_uid === null || lab.demo_uid === undefined) return '';

  const where = dataCenter
    ? labDataCenterCode(dataCenter).toUpperCase() || dataCenter.dcloud_dc_name
    : lab.demo_data_center
      ? `dc ${lab.demo_data_center}`
      : 'data centre no longer recorded';

  return [`demo ${lab.demo_uid}`, lab.demo_name || null, where].filter(Boolean).join(' • ');
};

/** A configuration item in one line: what it does, and whether it is pre-built. */
export const labItemSummary = (item?: LabConfigurationItem | null): string => {
  if (!item) return '-';

  return [
    labItemOperationLabel(item.operation),
    item.pre_configure ? 'already in the template' : null,
    item.device_type_name || item.device_type_key || null,
    (item.prerequisite_keys || []).length
      ? `needs ${(item.prerequisite_keys || []).length}`
      : null,
  ]
    .filter(Boolean)
    .join(' • ');
};

/** A method in one line: what the agent will actually be asked to do, and over what. */
export const labMethodSummary = (method?: LabExecutionMethod | null): string => {
  if (!method) return '-';

  return [
    method.action_type_label || labActionTypeLabel(method.action_type),
    method.transport ? `over ${labTransportLabel(method.transport)}` : 'the device\'s transport',
    method.supports_check ? null : 'cannot check anything',
    method.enabled ? null : 'disabled',
  ]
    .filter(Boolean)
    .join(' • ');
};

/** A device type in one line. The product is named where there is one, since that is the point. */
export const labDeviceTypeSummary = (type?: LabDeviceType | null): string => {
  if (!type) return 'Unclassified';

  return [
    // A family says so instead of naming a product, because it has none to name: the product
    // is answered per device, and printing this type's blank `product_name` would read as
    // "not a Cisco product" - the opposite of what it means.
    type.requires_product ? 'a family - each device names its product' : type.product_name || null,
    `reached over ${labTransportLabel(type.default_transport)}`,
    (type.method_keys || []).length
      ? `${(type.method_keys || []).length} method${(type.method_keys || []).length === 1 ? '' : 's'}`
      : 'any method',
    type.enabled ? null : 'disabled',
  ]
    .filter(Boolean)
    .join(' • ');
};

/**
 * Why this method will not do what its name suggests.
 *
 * The one that matters is the `supports_check` pairing. A method that only applies - terraform,
 * most of all - reports its own exit code, so using it as a validation or completion method
 * scores "the tool ran" as "the lab worked", which passes every time whether or not the pod is
 * in the state the item claims.
 */
export const labMethodBlockers = (method: LabExecutionMethod): string[] => {
  const blockers: string[] = [];

  if (!method.action_type) {
    blockers.push(
      'No action type, so nothing says what the agent is actually asked to do. It compiles to nothing.',
    );
  }

  if (!method.enabled) {
    blockers.push('Disabled. Items keep referring to it and none of their steps are generated.');
  }

  if (!method.supports_check) {
    blockers.push(
      'Cannot observe anything, so it is only honest as an action method. Used for validation, health or completion it scores the tool exiting zero as the lab being right.',
    );
  }

  return blockers;
};

/**
 * Which of an item's four methods this device type cannot deliver.
 *
 * The same check the attach endpoint applies, run early so it reads as a reason the option is
 * not offered rather than a 400 after the choice. An empty method list on the type means
 * unconstrained, not "nothing allowed" - an under-specified list would refuse real work.
 */
export const labItemMethodConflicts = (
  item: LabConfigurationItem,
  type?: LabDeviceType | null,
): string[] => {
  if (!type || !(type.methods || []).length) return [];

  const allowed = new Set(type.methods || []);

  return (
    [
      ['action', item.action_method, item.action_method_key],
      ['validation', item.validation_method, item.validation_method_key],
      ['health check', item.health_method, item.health_method_key],
      ['completion', item.completion_method, item.completion_method_key],
    ] as [string, number | null, string | undefined][]
  )
    .filter(([, id]) => !!id && !allowed.has(id as number))
    .map(([field, , key]) => `${type.name} cannot be reached by ${key || 'that method'} (${field}).`);
};

/** What a compile will substitute for one attachment: the item's defaults, then the overrides. */
export const labEffectiveOptions = (
  item?: LabConfigurationItem | null,
  attachment?: LabScenarioItem | null,
): Record<string, any> => ({
  ...(item?.options || {}),
  ...(attachment?.option_overrides || {}),
});

/**
 * What an item will and will not contribute once compiled.
 *
 * The two that matter are silent. An item with no action and no validation is attached and has
 * no effect at all, and an item whose action does not state `disruptive` and `blast_radius`
 * compiles into a step the fleet gate refuses to fan out - both look like working content on
 * every screen that lists them.
 */
export const labItemBlockers = (item: LabConfigurationItem): string[] => {
  const blockers: string[] = [];
  const action = item.action || {};
  const validation = item.validation || {};

  if (!Object.keys(action).length && !Object.keys(validation).length) {
    blockers.push(
      'No action and no validation, so this compiles to nothing. Attaching it has no effect on a run.',
    );
  }

  if (item.pre_configure && !Object.keys(validation).length) {
    blockers.push(
      'Pre-configured with no validation: the run starts past this and never checks the template really arrived with it.',
    );
  }

  if (Object.keys(action).length) {
    if (action.disruptive === undefined || action.disruptive === null || !action.blast_radius) {
      blockers.push(
        'The action does not state disruptive and blast_radius, so its step is excluded from fleet-wide sweeps.',
      );
    }

    // The action type can come from either place, so neither alone is a blocker. Both absent
    // means the compile falls back to a shell command, which is right for almost nothing.
    if (!item.action_method && !action.action_type) {
      blockers.push(
        'Neither an action method nor an action_type in the spec, so the compile falls back to a plain shell command.',
      );
    }
  }

  if (Object.keys(item.health_check || {}).length && !item.health_method) {
    blockers.push(
      'A health check with no health method, so nothing repeats to observe it - the devices are only marked monitored.',
    );
  }

  if (!item.device_type) {
    blockers.push(
      'No device type, so nothing checks this is attached to the kind of box it was written for.',
    );
  }

  if (item.pre_configure && item.operation === 'simulate') {
    blockers.push(
      'A simulated problem that is pre-built into the template hands the learner the answer. Check this is what you meant.',
    );
  }

  if (!item.enabled) {
    blockers.push('Disabled, so it stays attached to every scenario and compiles to nothing.');
  }

  return blockers;
};

/**
 * Why this lab will not deliver anything yet.
 *
 * All of these are silent. A lab with no sections is a row that looks authored and brings a bare
 * dCloud template up; a lab published nowhere is never matched to a session at all; a lab
 * whose every section is disabled does open runs, against nothing; and a lab with no demo recorded
 * runs perfectly well while leaving nobody able to say what it was built against.
 */
export const labBlockers = (lab: Lab): string[] => {
  const blockers: string[] = [];
  const links = lab.scenario_links || [];
  const live = links.filter((link) => link.enabled && link.scenario_enabled !== false);

  if (!links.length) {
    blockers.push('No sections, so this delivers the plain dCloud template and nothing else.');
  } else if (!live.length) {
    blockers.push(
      `All ${links.length} section${links.length === 1 ? '' : 's'} disabled, so runs open and do nothing.`,
    );
  }

  if (!(lab.configurations || []).length) {
    blockers.push(
      'Not published in any data centre, so no session will ever be matched to it. Add a publication.',
    );
  }

  // Silent in a different way to the rest: a lab with no demo recorded still runs, because what a
  // session is matched on is the demo id on each publication. What is lost is the authoring trail -
  // nothing says which dCloud environment the sections were written against, so the next person to
  // change one has to infer it from the publications, and there may be several or none.
  if (!lab.demo_uid) {
    blockers.push(
      'No dCloud demo recorded, so nothing says which environment this lab was built from.',
    );
  }

  if (!lab.enabled) {
    blockers.push('Disabled. Sessions are not matched against it at all.');
  }

  return blockers;
};

/**
 * Why this device cannot be reached, or will not be checked.
 *
 * A device is the one row in the tree that nothing else can compensate for: an item compiles into a
 * step, and the step is leased against a device by address. So an address that is absent or still a
 * template token is a run that fails at connect time, having reported every earlier screen as
 * fine - and because an address can also be supplied per publication by `LabDeviceAddress`, the
 * absence is stated as a condition rather than a verdict.
 *
 * `credential_ref` names a credential and is never one. A device with no reference has nothing to
 * authenticate with; the fix is a reference to a stored credential, never a password typed here.
 *
 * `context.jumpHost` is optional because not every caller has the row: a jump host is a device
 * like any other and may not be attached to the section being looked at. Pass it where it is to
 * hand - the library list has every device - and the shared-library hazard below gets checked.
 */
export const labDeviceBlockers = (
  device: LabDevice,
  context: { jumpHost?: LabDevice | null } = {},
): string[] => {
  const blockers: string[] = [];
  const address = (device.address_template || '').trim();

  if (!address) {
    blockers.push(
      'No address, so nothing can connect unless every publication of every lab using this section overrides it.',
    );
  } else if (/\{\{|\{%/.test(address)) {
    blockers.push(
      `The address is still a template (${address}), so it only resolves if the run supplies that value.`,
    );
  }

  if (!device.credential_ref) {
    blockers.push(
      'No credential reference, so there is nothing to authenticate with. Name a stored credential - never a password.',
    );
  }

  if (!device.device_type) {
    blockers.push(
      'No device type, so nothing checks that the items attached here were written for this kind of box.',
    );
  }

  if (device.device_type_requires_product && !device.product_uid) {
    blockers.push(
      `${device.device_type_name || 'This device type'} covers several Cisco products and each has its own API. ` +
        'Pick which product this box is, or nothing knows which spec to speak to it.',
    );
  }

  if (device.required && !device.monitored) {
    blockers.push(
      'Required but not monitored: a run depends on this device and nothing observes whether it stayed up.',
    );
  }

  // The hazard a shared library introduces, and the reason this check exists at author time:
  // a jump host is a device, devices are attached per section, and nothing stops this box being
  // attached to a section its jump host is not. At run time that resolves to no jump host at all
  // rather than to an error, so the connection is attempted directly from wherever the agent sits
  // and the failure reads as "the device is down".
  if (device.jump_host && context.jumpHost) {
    const reachable = new Set(
      (context.jumpHost.scenario_links || []).map((link) => link.scenario),
    );
    const stranded = (device.scenario_links || [])
      .filter((link) => !reachable.has(link.scenario))
      .map((link) => link.scenario_name || link.scenario_slug || `scenario ${link.scenario}`);

    if (stranded.length) {
      blockers.push(
        `Reached through ${context.jumpHost.device_key}, which is not attached to ${stranded.join(', ')}. ` +
          'A run of those sections treats this device as directly reachable. Attach the jump host there too.',
      );
    }
  }

  return blockers;
};

/**
 * Whether an override row states the same thing the device already says.
 *
 * Worth flagging rather than blocking: a row that matches the default is not wrong, it is
 * noise - and noise in this table is how somebody later "fixes" the device address and finds
 * nothing changed, because a redundant override was pinning it.
 */
export const labDeviceAddressIsRedundant = (row: LabDeviceAddress): boolean => {
  const addressMatches =
    !row.address_template || row.address_template === (row.device_address_template || '');
  const portMatches = row.port === null || row.port === undefined || row.port === row.device_port;

  return addressMatches && portMatches;
};

/**
 * What a publication actually reaches a device at. The same precedence the runtime applies.
 *
 * Duplicated from `resolve_device_address` in toolbox_dcloud deliberately, and only for display:
 * the builder has to show the effective address beside the default, and an admin who cannot see
 * what a run will use cannot tell a working override from a typo. Nothing here is sent - the
 * agent's work order is resolved server-side.
 */
export const labEffectiveDeviceAddress = (
  device: LabDevice,
  override?: LabDeviceAddress | null,
): { address: string; port: number | null; overridden: boolean } => {
  if (!override) {
    return { address: device.address_template || '', port: device.port ?? null, overridden: false };
  }

  const address = override.address_template || device.address_template || '';
  const port = override.port === null || override.port === undefined ? device.port ?? null : override.port;

  return {
    address,
    port,
    overridden: address !== (device.address_template || '') || port !== (device.port ?? null),
  };
};

/**
 * The prerequisite cycle a proposed edit would create, as the path that closes it.
 *
 * Checked in the UI because the two ways of finding out otherwise are both bad: the attach
 * expands prerequisites transitively, so a cycle is either an infinite walk server-side or a
 * quietly truncated one, and neither tells the author which two items disagree. Returns the
 * path - `['vpn', 'directory', 'vpn']` - because "circular dependency" without the names is
 * a message that sends somebody to read nine rows.
 *
 * `proposed` is the prerequisite list being considered for `itemId`, which is why this cannot
 * just walk the catalogue: the edge that closes the loop is not saved yet.
 */
export const labPrerequisiteCycle = (
  itemId: number,
  proposed: number[],
  catalogue: LabConfigurationItem[],
): string[] | null => {
  const byId = new Map(catalogue.map((item) => [item.id, item]));
  const edges = (id: number): number[] =>
    id === itemId ? proposed : (byId.get(id)?.prerequisites as number[] | undefined) || [];
  const label = (id: number): string => byId.get(id)?.key || `item ${id}`;

  // Depth-first from each proposed prerequisite, looking for the way back to `itemId`. Only
  // that direction matters: a cycle elsewhere in the catalogue is not this edit's doing and
  // refusing the save for it would make an unrelated row unsaveable.
  const walk = (id: number, path: number[]): number[] | null => {
    if (id === itemId) return [...path, id];
    if (path.includes(id)) return null;

    for (const next of edges(id)) {
      const found = walk(next, [...path, id]);
      if (found) return found;
    }

    return null;
  };

  for (const start of proposed) {
    const found = walk(start, [itemId]);
    if (found) return found.map(label);
  }

  return null;
};

/**
 * Why this configuration will not deliver anything yet.
 *
 * Every one of these is a thing that fails quietly rather than loudly: a configuration with no
 * demo id matches no session, so sessions come up and no run ever opens against them, and
 * nothing in the logs says why.
 */
export const labConfigurationBlockers = (configuration: LabConfiguration): string[] => {
  const blockers: string[] = [];

  if (!configuration.lab && !configuration.scenario) {
    blockers.push(
      'Bound to neither a lab nor a scenario, so a matched session would find nothing to run.',
    );
  }

  if (!configuration.demo_uid && !configuration.demo_name) {
    blockers.push('No demo id or name, so no session in this data centre will ever match it.');
  }

  if (!configuration.template_topology_uid) {
    blockers.push('No dCloud template recorded, so nothing says what this lab was published from.');
  }

  if (!configuration.enabled) {
    blockers.push('Disabled. Sessions are not matched against it at all.');
  }

  return blockers;
};

/**
 * Why this event cannot hand a pod to anybody yet.
 *
 * `is_ready` is the backend's own verdict and is preferred where it is present; these are the
 * reasons behind it, so the screen can say which half is missing rather than just "not ready".
 */
export const labEventBlockers = (event: LabEvent): string[] => {
  const blockers: string[] = [];

  if (event.mode === 'single' && !event.session) {
    blockers.push('One-session event with no session. Nothing will come up.');
  }

  if (event.mode === 'pool' && !(event.pools || []).length) {
    blockers.push('Pool event with no pool. There is nothing to stock or hand out.');
  }

  if (event.state === 'draft') {
    blockers.push('Still a draft.');
  }

  if (event.state === 'cancelled') {
    blockers.push('Cancelled.');
  }

  return blockers;
};
